export function getCurrentPageLocation(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isAbsolutePageLocation(pageLocation: string): boolean {
  return (
    pageLocation.startsWith("http://") || pageLocation.startsWith("https://")
  );
}

function appendAssessmentStepToPageLocation(
  pageLocation: string,
  stepKey: string,
): string {
  const normalizedPageLocation = pageLocation.trim();
  const normalizedStepKey = stepKey.trim();
  if (!normalizedPageLocation || !normalizedStepKey) {
    return normalizedPageLocation || "/";
  }

  if (isAbsolutePageLocation(normalizedPageLocation)) {
    const nextUrl = new URL(normalizedPageLocation);
    nextUrl.hash = `assessmentStep=${encodeURIComponent(normalizedStepKey)}`;
    return nextUrl.toString();
  }

  const [withoutHash] = normalizedPageLocation.split("#", 1);
  return `${withoutHash}#assessmentStep=${encodeURIComponent(normalizedStepKey)}`;
}

export function buildAssessmentPageLocation(stepKey?: unknown): string {
  const normalizedStepKey =
    typeof stepKey === "string" && stepKey.trim() ? stepKey.trim() : null;
  const basePageLocation = getCurrentPageLocation();

  if (!normalizedStepKey) {
    return basePageLocation;
  }

  return appendAssessmentStepToPageLocation(
    basePageLocation,
    normalizedStepKey,
  );
}

export function parseAssessmentPathname(pageLocation: string): string {
  const normalizedPageLocation = pageLocation.trim();
  if (!normalizedPageLocation) {
    return "/";
  }

  if (isAbsolutePageLocation(normalizedPageLocation)) {
    return new URL(normalizedPageLocation).pathname;
  }

  const [pathname] = normalizedPageLocation.split(/[?#]/, 1);
  if (!pathname) {
    return "/";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

export function resolveAssessmentTestType(
  pageLocation: string,
): "reading" | "listening" | null {
  const pathname = parseAssessmentPathname(pageLocation);

  if (pathname.startsWith("/reading")) {
    return "reading";
  }

  if (pathname.startsWith("/listening")) {
    return "listening";
  }

  return null;
}

export function resolveAssessmentSubmissionId(
  pageLocation: string,
): string | null {
  const pathname = parseAssessmentPathname(pageLocation);
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length < 2) {
    return null;
  }

  if (segments[1] === "demo") {
    return null;
  }

  return segments[1]?.trim() || null;
}

export function isAssessmentWelcomePath(pageLocation: string): boolean {
  const pathname = parseAssessmentPathname(pageLocation);
  return pathname === "/reading" || pathname === "/listening";
}
